import React, { useState, useEffect, useRef } from 'react';
import { getTournaments, getTournament, adminCreateTournament, adminUpdateTournament, adminDeleteTournament, adminUploadTournamentImage } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Trophy, Users, Eye, Upload, ImageIcon, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminTournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailTournament, setDetailTournament] = useState(null);
  const [form, setForm] = useState({ name: '', date: '', description: '', prize_money: '', max_participants: '', location: '', registration_deadline: '' });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);

  const fetchData = async () => {
    try {
      const res = await getTournaments();
      setTournaments(res.data);
    } catch (error) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  const resetForm = () => setForm({ name: '', date: '', description: '', prize_money: '', max_participants: '', location: '', registration_deadline: '' });

  const handleSave = async () => {
    try {
      const data = { ...form, prize_money: form.prize_money ? parseFloat(form.prize_money) : null, max_participants: form.max_participants ? parseInt(form.max_participants) : null };
      let tournamentId;
      if (editing) {
        await adminUpdateTournament(editing.tournament_id, data);
        tournamentId = editing.tournament_id;
        toast.success('Turnier aktualisiert');
      } else {
        const res = await adminCreateTournament(data);
        tournamentId = res.data.tournament_id;
        toast.success('Turnier erstellt');
      }
      if (imageFile && tournamentId) {
        const fd = new FormData();
        fd.append('file', imageFile);
        await adminUploadTournamentImage(tournamentId, fd);
      }
      setDialogOpen(false); setEditing(null); setImageFile(null); resetForm(); fetchData();
    } catch (error) { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`"${t.name}" löschen?`)) return;
    try { await adminDeleteTournament(t.tournament_id); toast.success('Gelöscht'); fetchData(); }
    catch (error) { toast.error('Fehler'); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setImageFile(null);
    setForm({ name: t.name, date: t.date || '', description: t.description || '', prize_money: t.prize_money?.toString() || '', max_participants: t.max_participants?.toString() || '', location: t.location || '', registration_deadline: t.registration_deadline || '' });
    setDialogOpen(true);
  };

  const handleShowDetail = async (t) => {
    try {
      const res = await getTournament(t.tournament_id);
      setDetailTournament(res.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden');
    }
  };

  const handleImageUploadExisting = async (tournamentId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(tournamentId);
      try {
        const fd = new FormData();
        fd.append('file', file);
        await adminUploadTournamentImage(tournamentId, fd);
        toast.success('Bild hochgeladen');
        fetchData();
      } catch (err) { toast.error('Upload fehlgeschlagen'); }
      finally { setUploading(null); }
    };
    input.click();
  };

  return (
    <div data-testid="admin-tournaments-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-bold uppercase">Turniere</h1>
        <Button onClick={() => { setEditing(null); setImageFile(null); resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-tournament-btn">
          <Plus className="w-4 h-4 mr-2" />Turnier
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">{[1, 2].map(i => <Card key={i} className="card-surface animate-pulse"><CardContent className="p-6"><div className="h-16 bg-white/10 rounded" /></CardContent></Card>)}</div>
      ) : tournaments.length > 0 ? (
        <div className="space-y-4">
          {tournaments.map(t => (
            <Card key={t.tournament_id} className="card-surface">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {t.image_url ? (
                    <img src={getPhotoUrl(t.image_url)} alt="" className="w-16 h-16 object-cover rounded-sm" />
                  ) : (
                    <div className="w-14 h-14 bg-primary/10 rounded-sm flex flex-col items-center justify-center">
                      <span className="text-lg font-heading font-bold text-primary">{new Date(t.date).getDate()}</span>
                      <span className="text-xs text-text-secondary">{new Date(t.date).toLocaleDateString('de-DE', { month: 'short' })}</span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading font-bold uppercase">{t.name}</h3>
                    <p className="text-text-secondary text-sm">{t.location || 'Kein Ort'} {t.prize_money ? `\u2022 ${t.prize_money}\u20AC Preisgeld` : ''}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Users className="w-3.5 h-3.5 text-accent" />
                      <span className="text-sm text-accent font-medium">{t.current_participants || 0} Anmeldungen</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" className="text-accent" onClick={() => handleShowDetail(t)} data-testid={`view-tournament-regs-${t.tournament_id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-accent" onClick={() => handleImageUploadExisting(t.tournament_id)} disabled={uploading === t.tournament_id} title="Bild/Flyer hochladen">
                    {uploading === t.tournament_id ? <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)}><Edit className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(t)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-surface"><CardContent className="p-8 text-center"><Trophy className="w-12 h-12 text-text-secondary mx-auto mb-4" /><p className="text-text-secondary">Keine Turniere.</p></CardContent></Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle>{editing ? 'Turnier bearbeiten' : 'Neues Turnier'}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2"><Label>Name *</Label><Input className="input-dark" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="tournament-name-input" /></div>
            <div className="space-y-2"><Label>Datum *</Label><Input type="date" className="input-dark" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="space-y-2"><Label>Ort</Label><Input className="input-dark" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Preisgeld</Label><Input type="number" className="input-dark" value={form.prize_money} onChange={e => setForm({ ...form, prize_money: e.target.value })} /></div>
              <div className="space-y-2"><Label>Max. Teilnehmer</Label><Input type="number" className="input-dark" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Anmeldeschluss</Label><Input type="date" className="input-dark" value={form.registration_deadline} onChange={e => setForm({ ...form, registration_deadline: e.target.value })} /></div>
            <div className="space-y-2"><Label>Beschreibung</Label><Textarea className="input-dark" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Bild / Flyer</Label>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => setImageFile(e.target.files?.[0] || null)} />
              {imageFile ? (
                <div className="relative">
                  <img src={URL.createObjectURL(imageFile)} alt="" className="w-full h-40 object-cover rounded-sm" />
                  <button onClick={() => { setImageFile(null); if (fileRef.current) fileRef.current.value = ''; }} className="absolute top-2 right-2 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center">
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : editing?.image_url ? (
                <img src={getPhotoUrl(editing.image_url)} alt="" className="w-full h-40 object-cover rounded-sm" />
              ) : null}
              <Button type="button" variant="outline" className="w-full border-dashed border-white/20 text-text-secondary" onClick={() => fileRef.current?.click()}>
                <ImageIcon className="w-4 h-4 mr-2" />
                {imageFile ? 'Anderes Bild wählen' : 'Bild / Flyer hochladen'}
              </Button>
            </div>

            <Button onClick={handleSave} className="btn-primary w-full" data-testid="save-tournament-btn">{editing ? 'Aktualisieren' : 'Erstellen'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Registrations Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader>
            <DialogTitle>Anmeldungen - {detailTournament?.name}</DialogTitle>
          </DialogHeader>
          {detailTournament && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-accent" />
                <span className="font-medium">{detailTournament.current_participants || 0} Teilnehmer</span>
              </div>
              {detailTournament.registrations && detailTournament.registrations.length > 0 ? (
                <div className="space-y-2">
                  {detailTournament.registrations.map((reg, i) => (
                    <div key={reg.registration_id || i} className="card-elevated p-3 flex items-center justify-between">
                      <span className="font-medium">{reg.user_name}</span>
                      <span className="text-xs text-text-secondary">{formatDate(reg.created_at)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-text-secondary text-sm">Noch keine Anmeldungen.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTournaments;
